import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

export type RecommendationsAdIndices = [number, number?]

type GetRecommendationsAdIndices = (
  queryClient: QueryClient,
) => RecommendationsAdIndices

const getRecommendationsAdIndices: GetRecommendationsAdIndices = (
  queryClient,
) =>
  queryClient.getQueryData<RecommendationsAdIndices>([
    'recommendations-ad-indices',
  ]) ?? [-1, -1]

type UseRecommendationsAdIndices = () => RecommendationsAdIndices

const useRecommendationsAdIndices: UseRecommendationsAdIndices = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<() => void>(undefined)

  const [recommendationsAdIndices, setRecommendationsAdIndices] =
    useState<RecommendationsAdIndices>(() =>
      getRecommendationsAdIndices(queryClient),
    )

  const updateRecommendationsAdIndices = useCallback(
    (args: any) => {
      if (
        ['invalidate'].includes(args?.action?.type) &&
        args.query.queryKey[0] === 'recommendations-ad-indices'
      ) {
        setRecommendationsAdIndices(getRecommendationsAdIndices(queryClient))
      }
    },
    [queryClient],
  )

  useEffect(() => {
    setRecommendationsAdIndices(getRecommendationsAdIndices(queryClient))

    unsubscribeFnRef.current = queryCache.subscribe(
      updateRecommendationsAdIndices,
    )

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, queryClient, updateRecommendationsAdIndices])

  return recommendationsAdIndices
}

export { getRecommendationsAdIndices }

export default useRecommendationsAdIndices
